import { HeartHandshake, ShieldCheck, Layers, Users } from "lucide-react";
import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { SectionHead, CTABand } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";

const VALUES = [
  { icon: HeartHandshake, title: "Person at the centre", desc: "Technology that keeps the person receiving support at the heart of every decision." },
  { icon: Layers, title: "One connected environment", desc: "Bringing the information care teams rely on into a single, joined-up platform." },
  { icon: ShieldCheck, title: "Visibility & oversight", desc: "Helping organisations improve consistency, quality and governance." },
  { icon: Users, title: "Built for care teams", desc: "Designed around the practical realities of modern care delivery." },
];

export default function About() {
  useSEO(
    "About AYOBELL | Care Management Technology by TechSpace Solutions",
    "AYOBELL brings the information care teams rely on into one connected environment — improving visibility, consistency and oversight. Developed by TechSpace Solutions Ltd."
  );
  return (
    <>
      <PageHero
        eyebrow="About AYOBELL"
        title="Technology should make care simpler, not create more administration."
        subtitle="AYOBELL was created to bring the information care teams rely on into one connected environment — keeping the person receiving support at the centre."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/platform", label: "Explore the Platform" }}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-lg text-slate-600 leading-relaxed">
              Care providers spend too much time managing systems and not enough time delivering great care. AYOBELL was created to change that — connecting care delivery, people, medication, workforce, governance and operational oversight in one secure platform.
            </p>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              By bringing this information together, AYOBELL helps organisations improve visibility, consistency and oversight while keeping the person receiving support at the centre of everything.
            </p>
            <p className="mt-5 text-slate-500 leading-relaxed">
              AYOBELL is developed by <span className="font-semibold text-slate-700">TechSpace Solutions Ltd</span>, designed around the practical realities of modern care delivery in the UK.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead eyebrow="What we believe" title="The principles behind AYOBELL" />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {VALUES.map((v) => (
              <StaggerItem key={v.title}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
                  <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 text-white"><v.icon className="w-5 h-5" /></span>
                  <h3 className="font-display font-bold text-lg mt-4 text-slate-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand title="See AYOBELL in action for your service." />
    </>
  );
}
